import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCartStore } from '../../stores/cartStore';
import { useAuthStore } from '../../stores/authStore';
import {
  AlertTriangle, CalendarX2, CheckCircle, ExternalLink, FileText, Home,
  Loader2, Mail, RefreshCw,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { euros } from '../../lib/orders/devis';
import { produireEtEnvoyerFacture } from '../../lib/orders/gateway.supabase';
import { QrDePaiement } from '../../components/checkout/QrDePaiement';
import { REGLE_ANNULATION_JPA, estUneFactureJpa } from '../../lib/jpa/regles';
import {
  lireIssueDePaiement, etatAffiche, messageCentral, type SoldeDeFacture,
} from '../../lib/paiement/stripe';

/*
  La page qui dit au parent que son enfant est inscrit.

  ## Ce qui n'allait pas

  Signale le 4 septembre 2026 : « Quand le panier est valide il n'y a plus la
  page qui dis que l'inscription est validee, on arrive sur un panier vide sans
  validation claire. »

  Deux causes distinctes, corrigees ensemble :

  1. **La redirection.** `CheckoutPage` vidait le panier AVANT de naviguer. Le
     rendu suivant voyait un panier vide et renvoyait vers `/cart`. Le panier
     est desormais vide ICI, une fois cette page affichee.
  2. **Le document et le courriel n'etaient jamais demandes.** `create_order`
     ne les produit pas — un PDF genere dans la transaction survivrait a son
     annulation. Personne ne prenait le relais apres le COMMIT.

  ## Ce que cette page garantit

  Le message central est **l'inscription**, pas la facture :

    « il est tres important que le parent comprenne que son enfant est inscrit
      a nos plaines meme si le paiement ne nous arrive pas »

  Il est donc affiche immediatement, sans attendre le PDF ni le courriel. Ceux-
  ci arrivent apres, et leur echec est dit sans jamais laisser croire que
  l'inscription serait en cause.
*/

/** Le delai pendant lequel une famille peut encore se desinscrire. */
const JOURS_POUR_SE_DESINSCRIRE = 10;

const InvoiceConfirmationPage = () => {
  const { clearCart } = useCartStore();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();

  const [inscriptions, setInscriptions] = useState<any[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  /*
    Les parametres viennent de `create_order`. `?invoice=` (une URL de PDF)
    etait le parametre de l'ancien tunnel : on le lit encore pour les liens
    deja envoyes, mais on ne l'attend plus.
  */
  const invoiceNumber = searchParams.get('invoiceNumber');
  const invoiceId = searchParams.get('invoiceId');
  /*
    `?remaining=` n'est PLUS lu. Il a ete calcule au moment de la commande et ne
    bouge plus jamais : il affichait encore « Reste a payer : 3,00 € » sous un
    bandeau « Paiement recu ». Le solde se relit en base, plus bas. Le parametre
    reste dans l'URL pour les liens deja envoyes, sans rien decider.
  */
  const registrationIdsParam = searchParams.get('registrationIds');
  /*
    La communication STRUCTUREE, telle que `create_order` l'a rendue. C'est
    elle qui part dans le QR et dans le virement — pas le numero de facture,
    qu'aucune banque ne verifie.
  */
  const communication = searchParams.get('communication');
  /*
    L'issue du passage chez Stripe. Elle ne fait AUTORITE SUR RIEN : c'est le
    webhook qui ecrit le paiement, jamais ce retour de navigation. Un parent
    qui ferme son onglet apres avoir paye n'aura jamais `paiement=ok`, et sa
    facture sera reglee quand meme.

    Elle ne sert donc qu'a choisir le message. Et le message le plus important
    est le meme dans les deux cas : l'enfant est inscrit.
  */
  const issuePaiement = lireIssueDePaiement(searchParams.get('paiement'));

  const [pdfUrl, setPdfUrl] = useState<string | null>(searchParams.get('invoice'));
  const [envoi, setEnvoi] = useState<'attente' | 'en_cours' | 'fait' | 'echec'>('attente');
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null);
  /*
    Un parent qui rafraichit la page ne doit pas recevoir trois fois la meme
    facture. La reference tient sur les rendus ; le `pdf_url` deja enregistre
    sur la facture tient, lui, entre deux visites.
  */
  const envoiLance = useRef(false);
  /* Le solde tel que la BASE le dit. `null` tant qu'on ne l'a pas lu. */
  const [solde, setSolde] = useState<SoldeDeFacture | null>(null);

  const courriel = user?.email ?? null;

  const produireEtEnvoyer = useCallback(async (forcer = false) => {
    if (!invoiceNumber || !courriel) return;
    if (envoiLance.current && !forcer) return;
    envoiLance.current = true;

    setEnvoi('en_cours');
    setErreurEnvoi(null);

    /*
      Le garde-fou et l'envoi vivent dans la passerelle, pas ici : l'ecran
      d'inscription de l'administration passe par la meme etape, et une etape
      ecrite dans un ecran est une etape que le prochain ecran oublie.
    */
    const r = await produireEtEnvoyerFacture(invoiceNumber, courriel, { forcer });
    if (r.pdfUrl) setPdfUrl(r.pdfUrl);
    if (r.erreur) { setErreurEnvoi(r.erreur); setEnvoi('echec'); }
    else setEnvoi('fait');
  }, [invoiceNumber, courriel]);

  /*
    Le solde, relu en base. C'est la SEULE source de verite sur ce qui reste
    du : le parametre d'URL est fige a l'instant de la commande, et n'importe
    qui peut le reecrire a la main.

    Quand le parent revient de Stripe, le webhook a normalement deja ecrit —
    Stripe attend jusqu'a dix secondes sa reponse avant de rediriger. Quand ce
    n'est pas le cas, on redemande quelques fois plutot que d'afficher un
    « reste a payer » a quelqu'un qui vient de payer.
  */
  useEffect(() => {
    if (!invoiceId && !invoiceNumber) return;
    let annule = false;

    const lire = async (): Promise<SoldeDeFacture | null> => {
      /*
        Le cast est du a `src/lib/database.types.ts`, devenu obsolete : il ne
        connait pas `v_invoice_balances`, donc le nom de colonne se resout en
        `never` et `.eq()` refuse toute valeur. Regenerer ces types est un
        chantier a part — c'est la cause des 944 erreurs `tsc` du depot. On ne
        l'ouvre pas ici, et on ne laisse pas non plus l'erreur.
      */
      const requete = supabase
        .from('v_invoice_balances')
        .select('outstanding_cents, settlement') as unknown as {
          eq: (colonne: string, valeur: string) => {
            maybeSingle: () => Promise<{ data: SoldeDeFacture | null }>;
          };
        };
      const cle = invoiceId ? 'invoice_id' : 'invoice_number';
      const valeur = invoiceId ?? invoiceNumber;
      if (!valeur) return null;
      const { data } = await requete.eq(cle, valeur).maybeSingle();
      return data ?? null;
    };

    (async () => {
      /* Sept essais espaces de deux secondes : un peu plus que l'attente de Stripe. */
      const essais = issuePaiement === 'ok' ? 7 : 1;
      for (let i = 0; i < essais && !annule; i++) {
        const s = await lire();
        if (annule) return;
        if (s) {
          setSolde(s);
          /* On ne redemande que tant que le paiement annonce n'est pas visible. */
          if (issuePaiement !== 'ok' || Number(s.outstanding_cents) <= 0) return;
        }
        if (i < essais - 1) await new Promise((r) => setTimeout(r, 2000));
      }
    })();

    return () => { annule = true; };
  }, [invoiceId, invoiceNumber, issuePaiement]);

  /* Ce que l'ecran a le droit de dire, une fois le solde connu. */
  const etat = etatAffiche(issuePaiement, solde);
  /*
    La phrase centrale suit l'etat. Elle ne peut plus dire « meme si votre
    paiement ne nous est pas encore parvenu » a quelqu'un qui vient de payer.
  */
  const message = messageCentral(etat.bandeau);

  /*
    Une journee pedagogique se commande hors du panier : sa confirmation porte
    `panier=garder`, et un panier de stages en cours survit a l'inscription.
  */
  const garderLePanier = searchParams.get('panier') === 'garder';
  useEffect(() => {
    /* Le panier est vide ICI, jamais avant : voir l'en-tete du fichier. */
    if (!garderLePanier) clearCart();
  }, [clearCart, garderLePanier]);

  /*
    Le document n'est demande QUE quand l'etat est sur. Sinon on enverrait une
    facture « a payer dans les 20 jours » a un parent qui vient de regler — le
    generateur lit `outstanding_cents`, donc il l'imprimerait telle quelle.
  */
  useEffect(() => {
    if (!etat.peutProduireLaFacture) return;
    void produireEtEnvoyer();
  }, [etat.peutProduireLaFacture, produireEtEnvoyer]);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        setChargement(true);
        let requete = supabase
          .from('registrations')
          .select(`
            id, payment_status, amount_paid, price_type, invoice_id, invoice_url,
            kids!inner (prenom, nom),
            session:activity_id(
              stage:stage_id(title), start_date, end_date, center:center_id(name)
            )
          `)
          .order('created_at', { ascending: false });

        if (registrationIdsParam) {
          try {
            const ids = JSON.parse(registrationIdsParam);
            if (Array.isArray(ids) && ids.length > 0) requete = requete.in('id', ids);
          } catch {
            /* Un parametre illisible ne doit pas priver le parent de sa page. */
          }
        } else {
          requete = requete.limit(5);
        }

        const { data, error } = await requete;
        if (error) throw error;
        if (!annule) setInscriptions(data ?? []);
      } catch (e) {
        if (!annule) setErreur('Le détail des inscriptions n\'a pas pu être chargé.');
      } finally {
        if (!annule) setChargement(false);
      }
    })();
    return () => { annule = true; };
  }, [registrationIdsParam]);

  const dateLimite = new Date();
  dateLimite.setDate(dateLimite.getDate() + JOURS_POUR_SE_DESINSCRIRE);

  return (
    <div className="container max-w-3xl mx-auto py-12 px-4 animate-fade-in">
      {/*
        --- L'issue du paiement, quand le parent revient de Stripe ---
        Un bandeau, au-dessus de la carte. Il ne remplace jamais le message
        central : meme sur un echec, la premiere chose ecrite en gros reste
        « votre enfant est inscrit ». C'est la demande, mot pour mot — « il
        faut que ce soit tres clair qu'il est bien inscrit ! » — et c'est
        pourquoi l'echec n'a droit qu'a un bandeau ambre, jamais rouge, et
        jamais en tete de page.
      */}
      {etat.bandeau === 'echec' && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-lg font-bold text-amber-900">
                Oups — le paiement n'a pas abouti.
              </p>
              <p className="mt-1 text-amber-900">
                <span className="font-semibold">Votre enfant reste inscrit. Rien n'est
                perdu.</span>{' '}
                Sa place est reservee. Seul le paiement n'est pas passe.
              </p>
              <p className="mt-2 text-sm text-amber-800">
                Vous n'avez rien a refaire : votre facture vous attend ci-dessous,
                payable par virement dans les 15 jours. Inutile de vous reinscrire.
              </p>
            </div>
          </div>
        </div>
      )}

      {/*
        Pas de bandeau quand c'est paye : le TITRE le dit deja, et le repeter
        dans deux blocs differents est precisement ce qui a rendu la page
        confuse. Les deux autres bandeaux, eux, ajoutent une information que le
        titre ne porte pas.
      */}

      {/*
        Le parent revient de Stripe mais la base n'a pas encore enregistre. On
        dit l'attente — jamais « reste a payer », qui dementirait ce qu'il vient
        de faire, et jamais « paye », qu'on ne sait pas encore.
      */}
      {etat.bandeau === 'en_attente' && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
          <div className="flex items-start gap-3">
            <Loader2 className="h-6 w-6 text-blue-600 flex-shrink-0 animate-spin" />
            <div>
              <p className="text-lg font-bold text-blue-900">
                Confirmation du paiement en cours…
              </p>
              <p className="mt-1 text-sm text-blue-800">
                Votre banque a accepté. Nous attendons la confirmation de Stripe,
                d'ordinaire quelques secondes.{' '}
                <span className="font-semibold">
                  Vous n'avez rien d'autre à faire : votre enfant est inscrit et
                  votre paiement est parti.
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-8">
          {/* --- Le message central : l'enfant est inscrit --- */}
          <div className="flex justify-center mb-6">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle size={48} className="text-green-600" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            {message.titre}
          </h1>

          {/*
            La raison d'etre de la page : le parent doit comprendre que sa place
            est acquise. Le mot exact suit l'etat du paiement — voir
            `messageCentral`, et le defaut du 6 septembre qui l'a rendue
            necessaire.
          */}
          <p className="text-lg text-gray-700 mb-6 max-w-xl mx-auto text-center">
            {message.avant}
            <span className="font-semibold">{message.fort}</span>
            {message.apres}
          </p>

          {/* --- Le droit de se désinscrire --- */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 mb-8 flex items-start gap-3">
            <CalendarX2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              {/* Une journée pédagogique suit sa propre règle : elle compte à rebours depuis la journée. */}
              {estUneFactureJpa(invoiceNumber) ? (
                <p className="font-medium">{REGLE_ANNULATION_JPA}</p>
              ) : (
              <p className="font-medium">
                Vous pouvez encore vous désinscrire pendant {JOURS_POUR_SE_DESINSCRIRE} jours,
                soit jusqu'au {dateLimite.toLocaleDateString('fr-BE', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}.
              </p>
              )}
              <p className="mt-1">
                La demande se fait depuis <Link to="/registrations" className="underline font-medium">
                  vos inscriptions
                </Link>. Passé ce délai, l'annulation reste possible mais suit nos
                conditions de remboursement.
              </p>
            </div>
          </div>

          {/* --- La facture : ce qu'il reste à payer --- */}
          <div className="rounded-lg border border-gray-200 p-4 mb-6">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="flex-grow">
                <p className="font-medium text-gray-900">
                  {invoiceNumber ? `Facture ${invoiceNumber}` : 'Votre facture'}
                </p>

                {/*
                  Trois cas, parce qu'ils appellent trois actions differentes.
                  Annoncer « vous avez 15 jours pour payer » a une famille dont
                  la provision a deja tout regle serait faux — et c'est ce que
                  faisait l'ancien ecran, qui l'affichait toujours.
                */}
                {/*
                  Le reste vient du SOLDE RELU, jamais du parametre d'URL. Le
                  chiffre de l'URL date de la commande : il affichait encore
                  « Reste a payer : 3,00 € » sous un bandeau « Paiement recu ».
                */}
                {etat.resteCentimes === null ? (
                  <p className="text-gray-500 mt-1 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Lecture du solde…
                  </p>
                ) : etat.resteCentimes <= 0 ? (
                  <p className="text-green-700 font-medium mt-1">
                    {etat.bandeau === 'paye'
                      ? 'Payée en totalité. Rien à payer.'
                      : 'Rien à payer : le montant est entièrement couvert.'}
                  </p>
                ) : (
                  <p className="text-gray-700 mt-1">
                    Reste à payer : <span className="font-semibold">{euros(etat.resteCentimes)}</span>,
                    dans les 15 jours.
                  </p>
                )}

                {/* --- L'état de l'envoi, dit honnêtement --- */}
                <div className="mt-3 text-sm">
                  {envoi === 'en_cours' && (
                    <p className="flex items-center gap-2 text-gray-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Préparation de votre facture et envoi par email…
                    </p>
                  )}

                  {/* L'adresse d'envoi n'est pas dite : elle dépend de l'activité
                      (stages, journées pédagogiques), et ne sert pas au parent. */}
                  {envoi === 'fait' && (
                    <div className="text-gray-700">
                      <p className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-green-600" />
                        Envoyée à {courriel}.
                      </p>
                      <p className="mt-1 text-gray-600">
                        Vous ne la recevez pas dans les prochaines minutes ? Vérifiez votre
                        dossier de spam (courriers indésirables).
                      </p>
                    </div>
                  )}

                  {envoi === 'echec' && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                      <p className="flex items-start gap-2 text-amber-900">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <span className="font-medium">
                            L'envoi de la facture par email a échoué.
                          </span>
                          {' '}Votre inscription, elle, est bien enregistrée — ceci ne la
                          remet pas en cause. Vous pouvez réessayer, ou retrouver la
                          facture dans vos inscriptions.
                          {erreurEnvoi && (
                            <span className="block mt-1 text-xs text-amber-700">
                              Détail : {erreurEnvoi}
                            </span>
                          )}
                        </span>
                      </p>
                      <button
                        onClick={() => void produireEtEnvoyer(true)}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Réessayer l'envoi
                      </button>
                    </div>
                  )}
                </div>

                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    <FileText className="h-4 w-4" />
                    Voir ma facture
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/*
            --- Le paiement ---
            Rien a payer : pas de QR. Un code pour zero euro serait refuse par
            la banque, et surtout il ferait croire qu'il reste quelque chose.
          */}
          {etat.montrerVirement && etat.resteCentimes !== null && communication && (
            <div className="mb-6">
              <QrDePaiement
                communication={communication}
                remainingCents={etat.resteCentimes}
                invoiceNumber={invoiceNumber}
              />
            </div>
          )}

          {/* --- Le détail des inscriptions --- */}
          {chargement ? (
            <div className="flex justify-center my-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : erreur ? (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-8 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">
                {erreur} L'inscription est enregistrée : vous la retrouverez dans{' '}
                <Link to="/registrations" className="underline font-medium">vos inscriptions</Link>.
              </p>
            </div>
          ) : inscriptions.length > 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {inscriptions.length > 1 ? 'Les inscriptions confirmées' : "L'inscription confirmée"}
              </h2>
              <div className="space-y-4">
                {inscriptions.map((r) => (
                  <div key={r.id} className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div>
                        <h3 className="font-medium">{r.session?.stage?.title ?? 'Stage'}</h3>
                        <p className="text-sm text-gray-600">
                          {r.session?.start_date
                            && new Date(r.session.start_date).toLocaleDateString('fr-BE')}
                          {/* Une journée pédagogique n'a qu'une date. */}
                          {r.session?.end_date && r.session.end_date !== r.session.start_date
                            && ` – ${new Date(r.session.end_date).toLocaleDateString('fr-BE')}`}
                        </p>
                        <p className="text-sm text-gray-600">
                          Pour {r.kids?.prenom} {r.kids?.nom}
                        </p>
                        {r.session?.center?.name && (
                          <p className="text-sm text-gray-600">Centre : {r.session.center.name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Place réservée
                        </span>
                        {/* En euros à la belge : `13.4 €` disait 13,40 €. */}
                        <p className="text-sm font-medium mt-1">{euros(Math.round(Number(r.amount_paid ?? 0) * 100))}</p>
                        {String(r.price_type ?? '').includes('reduced') && (
                          <span className="inline-block text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded mt-1">
                            Tarif réduit
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>


            </div>
          ) : null}

          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/registrations" className="btn-primary inline-flex items-center">
              <FileText className="h-4 w-4 mr-2" /> Voir mes inscriptions
            </Link>
            <Link to="/" className="btn-outline inline-flex items-center">
              <Home className="h-4 w-4 mr-2" /> Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceConfirmationPage;
